import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CheckCircle, Loader2 } from 'lucide-react';
import { leadsApi } from '../../lib/api';
import { toast } from 'sonner';

const ContactSection = () => {
  const [conversionForm, setConversionForm] = useState({
    full_name: '',
    email: '',
    whatsapp: '',
    birth_date: '',
    country: '',
    study_level: '',
    professional_domain: '',
    objectif: '',
    learned_german: '',
    german_level: ''
  });
  const [isConversionSubmitting, setIsConversionSubmitting] = useState(false);
  const [isConversionSubmitted, setIsConversionSubmitted] = useState(false);

  const handleConversionSubmit = async (e) => {
    e.preventDefault();
    setIsConversionSubmitting(true);
    
    try {
      await leadsApi.create({
        name: conversionForm.full_name,
        email: conversionForm.email || null,
        whatsapp: conversionForm.whatsapp,
        birth_date: conversionForm.birth_date || null,
        country: conversionForm.country || null,
        study_level: conversionForm.study_level || null,
        professional_domain: conversionForm.professional_domain || null,
        objective: conversionForm.objectif,
        learned_german: conversionForm.learned_german === 'oui',
        german_level: conversionForm.learned_german === 'oui' ? conversionForm.german_level : null,
        source: 'landing_page'
      });
      setIsConversionSubmitted(true);
      setConversionForm({
        full_name: '', email: '', whatsapp: '', birth_date: '',
        country: '', study_level: '', professional_domain: '',
        objectif: '', learned_german: '', german_level: ''
      });
      toast.success('Demande envoyée ! Vous recevrez votre plan personnalisé sous 48h.');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setIsConversionSubmitting(false);
    }
  };

  const updateField = (field, value) => {
    setConversionForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-[#cc007e] relative overflow-hidden" data-testid="contact-section">
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-white/5 rounded-full blur-3xl" />
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Text Content (hidden on mobile) */}
          <div className="hidden md:block text-white text-center lg:text-left relative">
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-white/[0.08] rounded-full blur-xl" />
            <div className="absolute bottom-20 -left-20 w-48 h-48 dots-pattern opacity-50" />
            
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/20 text-white text-sm font-medium mb-6">
              Inscription gratuite
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
              Commencez votre
              <br />
              <span className="italic font-normal">parcours aujourd'hui ?</span>
            </h2>
            <p className="text-white/80 text-base mb-8 max-w-lg mx-auto lg:mx-0">
            </p>

            {/* Benefits */}
            <div className="space-y-4 mb-8">
              {[
                "Test de niveau gratuit inclus",
                "Conseiller dédié à votre projet",
                "Plan d'études personnalisé",
                "Financement flexible disponible"
              ].map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 justify-center lg:justify-start">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-white/90">{benefit}</span>
                </div>
              ))}
            </div>
            
          </div>

          {/* Right - Form */}
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl shadow-black/20">
            {isConversionSubmitted ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-gray-900 mb-3">
                  Demande reçue !
                </h3>
                <p className="text-gray-600 mb-6">
                  Un conseiller vous contactera dans les 48 heures pour discuter de votre projet.
                </p>
                
                {/* Optional email field after submission (mobile) */}
                <div className="md:hidden mb-6">
                  <p className="text-sm text-gray-500 mb-2">Ajoutez votre email pour recevoir une confirmation écrite (optionnel)</p>
                  <Input 
                    type="email"
                    placeholder="votre@email.com"
                    className="rounded-xl h-12"
                  />
                </div>
                
                <Button 
                  variant="outline" 
                  onClick={() => setIsConversionSubmitted(false)}
                  data-testid="contact-new-request-btn"
                >
                  Nouvelle demande
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-gray-900 mb-2">
                    Formulaire de contact
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base">
                    Gratuit et sans engagement - Réponse sous 48h
                  </p>
                </div>
                
                <form onSubmit={handleConversionSubmit} className="space-y-4" data-testid="contact-form">
                  {/* Name + Email */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="conv_name" className="text-gray-700">Nom complet *</Label>
                      <Input
                        id="conv_name"
                        value={conversionForm.full_name}
                        onChange={(e) => updateField('full_name', e.target.value)}
                        placeholder="Votre nom complet"
                        required
                        className="rounded-xl h-12"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="hidden sm:block space-y-2">
                      <Label htmlFor="conv_email" className="text-gray-700">Email *</Label>
                      <Input
                        id="conv_email"
                        type="email"
                        value={conversionForm.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        placeholder="votre@email.com"
                        required
                        className="rounded-xl h-12"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  
                  {/* WhatsApp + Date de naissance */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="conv_whatsapp" className="text-gray-700">WhatsApp *</Label>
                      <Input
                        id="conv_whatsapp"
                        type="tel"
                        value={conversionForm.whatsapp}
                        onChange={(e) => updateField('whatsapp', e.target.value)}
                        placeholder="+237 6XX XXX XXX"
                        required
                        className="rounded-xl h-12"
                        data-testid="contact-whatsapp-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="conv_birth_date" className="text-gray-700">Date de naissance</Label>
                      <Input
                        id="conv_birth_date"
                        type="date"
                        value={conversionForm.birth_date}
                        onChange={(e) => updateField('birth_date', e.target.value)}
                        className="rounded-xl h-12"
                        data-testid="contact-birth-date-input"
                      />
                    </div>
                  </div>

                  {/* Pays de résidence + Niveau d'études */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="conv_country" className="text-gray-700">Pays de résidence</Label>
                      <Input
                        id="conv_country"
                        value={conversionForm.country}
                        onChange={(e) => updateField('country', e.target.value)}
                        placeholder="Ex: Cameroun"
                        className="rounded-xl h-12"
                        data-testid="contact-country-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="conv_study_level" className="text-gray-700">Niveau d'études actuel</Label>
                      <Input
                        id="conv_study_level"
                        value={conversionForm.study_level}
                        onChange={(e) => updateField('study_level', e.target.value)}
                        placeholder="Ex: Licence, Master..."
                        className="rounded-xl h-12"
                        data-testid="contact-study-level-input"
                      />
                    </div>
                  </div>

                  {/* Domaine professionnel */}
                  <div className="space-y-2">
                    <Label htmlFor="conv_professional_domain" className="text-gray-700">Domaine professionnel (si applicable)</Label>
                    <Input
                      id="conv_professional_domain"
                      value={conversionForm.professional_domain}
                      onChange={(e) => updateField('professional_domain', e.target.value)}
                      placeholder="Ex: Informatique, Santé, Commerce..."
                      className="rounded-xl h-12"
                      data-testid="contact-professional-domain-input"
                    />
                  </div>

                  {/* Objectif */}
                  <div className="space-y-2">
                    <Label className="text-gray-700">Votre objectif *</Label>
                    <Select 
                      value={conversionForm.objectif} 
                      onValueChange={(value) => updateField('objectif', value)}
                      required
                    >
                      <SelectTrigger className="rounded-xl h-12" data-testid="contact-objective-select">
                        <SelectValue placeholder="Sélectionnez votre objectif" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="etudes">Études universitaires</SelectItem>
                        <SelectItem value="formation_pro">Formation professionnelle</SelectItem>
                        <SelectItem value="travail">Opportunité de travail</SelectItem>
                        <SelectItem value="regroupement">Regroupement familial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Avez-vous déjà appris l'allemand ? */}
                  <div className="space-y-2">
                    <Label className="text-gray-700">Avez-vous déjà appris l'allemand ? *</Label>
                    <Select
                      value={conversionForm.learned_german}
                      onValueChange={(value) => {
                        setConversionForm(prev => ({
                          ...prev,
                          learned_german: value,
                          german_level: value === 'non' ? '' : prev.german_level
                        }));
                      }}
                      required
                    >
                      <SelectTrigger className="rounded-xl h-12" data-testid="contact-learned-german-select">
                        <SelectValue placeholder="Sélectionnez" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="oui">Oui</SelectItem>
                        <SelectItem value="non">Non</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Conditional: German level if learned_german === 'oui' */}
                  {conversionForm.learned_german === 'oui' && (
                    <div className="space-y-2">
                      <Label className="text-gray-700">Quel est votre niveau ? *</Label>
                      <Select 
                        value={conversionForm.german_level} 
                        onValueChange={(value) => updateField('german_level', value)}
                        required
                      >
                        <SelectTrigger className="rounded-xl h-12" data-testid="contact-german-level-select">
                          <SelectValue placeholder="Sélectionnez votre niveau" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="debutant">Débutant</SelectItem>
                          <SelectItem value="a1">A1</SelectItem>
                          <SelectItem value="a2">A2</SelectItem>
                          <SelectItem value="b1">B1</SelectItem>
                          <SelectItem value="b2_plus">B2 ou plus</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Social proof line */}
                  <p className="text-sm text-center text-gray-500 pt-2">
                    Rejoignez <span className="font-semibold text-gray-700">45 étudiants</span> qui ont déjà fait confiance à Speak&Move
                  </p>
                  
                  {/* CTA Button */}
                  <Button 
                    type="submit" 
                    size="lg"
                    disabled={isConversionSubmitting || !conversionForm.objectif || !conversionForm.learned_german || (conversionForm.learned_german === 'oui' && !conversionForm.german_level)}
                    className="w-full bg-accent hover:bg-accent/90 rounded-xl py-7 text-lg font-bold cta-pulse-btn disabled:animate-none"
                    data-testid="contact-submit-btn"
                  >
                    {isConversionSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      'Recevoir mon plan personnalisé'
                    )}
                  </Button>
                  
                  <p className="text-xs text-center text-gray-500 mt-2">
                    Vos données sont sécurisées et ne seront jamais partagées.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
